import { z } from "zod";
import { DEFAULT_EDITION } from "./editions";
import {
  DEFAULT_TRILHA_KEY,
  LEGACY_FLORIPA_ONLY_TRILHA_KEYS,
  PROMO_PARTICIPATION_ROLE_KEYS,
  PROMO_TRILHA_KEYS,
} from "./promo-form-config";

export const ROLE_KEYS = PROMO_PARTICIPATION_ROLE_KEYS;

/**
 * TDC activity formats and current-edition trilhas. Legacy format keys and
 * Floripa-only trilha keys are kept so previously queued rows still validate.
 */
export const PALESTRA_TYPE_KEYS = [
  "palestra",
  "mentoria",
  "community-lounge",
  "workshop",
  ...LEGACY_FLORIPA_ONLY_TRILHA_KEYS,
  ...PROMO_TRILHA_KEYS,
] as const;

export const promoVideoSchema = z.object({
  role: z.enum(ROLE_KEYS),
  /**
   * Only meaningful when role === "palestrante". For the current edition this
   * selects the trilha and drives the badge color.
   */
  palestraType: z.enum(PALESTRA_TYPE_KEYS).optional().default(DEFAULT_TRILHA_KEY),
  speakerName: z.string().min(1).max(80),
  speakerTitle: z.string().max(120).optional().default(""),
  talkTitle: z.string().max(200).optional().default(""),
  /**
   * Public URL or data URL to the speaker photo. For the live Player preview
   * this will be an object URL (blob:); for the server render it's a data URL
   * so the headless browser can fetch it without hitting the network.
   */
  photoUrl: z.string().optional().default(""),
  /**
   * User-controlled translation applied to the speaker photo box, in
   * composition-space pixels (COMPOSITION_WIDTH × COMPOSITION_HEIGHT
   * coordinate system). Positive X = right, positive Y = down. The default
   * of 0/0 keeps the historical layout (photo anchored to the right edge,
   * bottom flush with the top of the bottom bar).
   *
   * Clamped to a generous range so a mis-computed value can't push the
   * photo fully off-screen.
   */
  photoOffsetX: z.number().min(-1200).max(1200).optional().default(0),
  photoOffsetY: z.number().min(-1600).max(1600).optional().default(0),
  /** User-controlled scale multiplier for the speaker photo box.
   *  1.0 = default size, 0.5 = half, 2.0 = double. */
  photoScale: z.number().min(0.3).max(2.0).optional().default(1),
  /**
   * Optional co-speaker. A non-empty `speaker2Name` switches the card to
   * the two-person split layout. Existing one-speaker payloads omit these
   * and keep the historical single-photo card.
   */
  speaker2Name: z.string().max(80).optional().default(""),
  speaker2Title: z.string().max(120).optional().default(""),
  photoUrl2: z.string().optional().default(""),
  photoOffsetX2: z.number().min(-1200).max(1200).optional().default(0),
  photoOffsetY2: z.number().min(-1600).max(1600).optional().default(0),
  photoScale2: z.number().min(0.3).max(2.0).optional().default(1),
  /**
   * When true with a non-empty speaker2Name, the card draws one full-width
   * pair photo from `photoUrl` instead of the split two-photo layout.
   * Ignored for one-speaker payloads.
   */
  sharedPhoto: z.boolean().optional().default(false),
  editionSlug: z.string().default(DEFAULT_EDITION),
});

/**
 * Input accepted by the async render queue. Requires an email so the worker
 * can deliver the finished video to the user.
 */
export const queuedPromoRequestSchema = promoVideoSchema.extend({
  email: z.string().email(),
});

export type QueuedPromoRequest = z.infer<typeof queuedPromoRequestSchema>;

export type PromoVideoProps = z.infer<typeof promoVideoSchema>;

// Duration constants (30 fps). The full composition runs for 15 seconds:
// a single background video plays edge-to-edge in black & white with the
// selected format color tinted on top; the TDC card elements fade in at
// 3 s, stay on screen through 12 s, and transition out in a 1-second
// window ending at 13 s. The background video keeps rolling the whole
// time so the last two seconds are pure atmospheric background.
export const FPS = 30;
export const TOTAL_FRAMES = 450; // 15 s
export const ELEMENTS_IN_FRAME = 90; // 3 s — card elements start fading in
export const ELEMENTS_EXIT_START_FRAME = 360; // 12 s — exit transition begins
export const ELEMENTS_EXIT_DURATION = 40; // ~1.3 s choreographed exit
/** Frame (inside the elements <Sequence>, not the composition) at which
 * AnimatedTDCCard should start fading out. Relative to the Sequence start
 * so AnimatedTDCCard can still use useCurrentFrame() naturally. */
export const CARD_EXIT_FRAME = ELEMENTS_EXIT_START_FRAME - ELEMENTS_IN_FRAME; // 270
export const CARD_DURATION_FRAMES = CARD_EXIT_FRAME + ELEMENTS_EXIT_DURATION; // 300

// 9:16 vertical — TikTok / Instagram Reels / YouTube Shorts / Stories.
export const COMPOSITION_WIDTH = 1080;
export const COMPOSITION_HEIGHT = 1920;

/**
 * Social-media safe area. Platforms like TikTok / Reels / Shorts cover
 * the top of the frame with the username and captions, and the bottom
 * with the caption text, hashtags, comment icon and CTA buttons. Keeping
 * the important content inside this rectangle guarantees nothing
 * important gets hidden, no matter the platform.
 *
 * Values tuned to be a conservative intersection of the three big ones.
 */
export const SAFE_TOP = 260;
export const SAFE_BOTTOM = 460;
export const SAFE_LEFT = 50;
export const SAFE_RIGHT = 50;
